import React, { useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { baseUrl } from "../../comman/config";
import { openConsumptionReport, trailingTwoYearsBeforeSession } from "../../comman/checkRas";
import { DateSessionFilter } from "../../components/Filter/DateSessionFilter";
import Loader from "../../components/Loader";
import NoRecordsFound from "../../components/NoRecordsFound";
import { ItemNameStacked, itemExportName } from '../../components/ItemName';
import * as XLSX from "xlsx";

// Rounds to 2 decimals for display, blank for empty/invalid.
const fmt = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? "" : Math.round(n * 100) / 100;
};
// FG / Person is read to 2 decimals on all three blocks, so it prints at a fixed 2 places --
// 20.5 gms reads as 20.50, not 20.5.
const fmt2 = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? "" : n.toFixed(2);
};

// Truncates typing in the editable FG / Person cell to the 2 decimals the column is planned to.
// step="any" allows anything otherwise. Truncated, not rounded, so a half-typed number does not
// jump a digit under the cursor.
const twoDecimals = (v) => {
  const s = String(v);
  const dot = s.indexOf(".");
  return dot === -1 ? s : s.slice(0, dot + 3);
};

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Value written back into an editable cell -- kept short so the two-way RM <-> FG/Person
// binding doesn't fill the input with float noise.
const round2 = (n) => (isFinite(n) ? Math.round(n * 100) / 100 : "");

// Planned Count and RM are whole-number fields. Everything after the decimal point is
// dropped, whether typed, pasted or read back from the DB. step="1" alone doesn't do this:
// browsers only validate against it on submit, they still accept "150.5" in the box.
const wholeNumber = (v) => {
  if (v === "" || v == null) return "";
  const n = parseFloat(v);
  return isNaN(n) ? "" : String(Math.trunc(n));
};

// Whole kgs, for the RM columns. Derived values round rather than truncate -- 12.7 kgs of
// raw material is nearer 13 than 12, and unlike a typed value there's no half-entered state.
const round0 = (n) => (isFinite(n) ? Math.round(n) : "");

// Blocks "." (and exponent notation) on the whole-number inputs. Sanitising in onChange alone
// isn't enough: a number input reports "" for the half-typed "150.", so the field would blank
// itself mid-keystroke. Paste and the spinner still go through wholeNumber above.
// The other half of the spinner. Blocking the wheel leaves the up and down arrows, which step a
// number input just the same -- and an arrow reaches the field WITHOUT the pointer being over it,
// so tabbing along a row and pressing Down to move on retyped the cell just left.
const blockArrowKeys = (e) => {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
};

// The whole-number fields: no spinner, and no decimal point or exponent either.
const blockWholeNumberKeys = (e) => {
  blockArrowKeys(e);
  if (e.key === "." || e.key === "e" || e.key === "E") e.preventDefault();
};

// A number input takes the mouse wheel as a spinner while it has focus, so scrolling the page
// with the pointer over a field silently retyped it -- and on this screen a changed Planned RM
// or FG / Person re-plans the other, so one scroll past a long menu could rewrite several rows
// without anybody touching a key. Dropping focus on the wheel is what every other screen here
// does (Recipe Planning, Kit Requisition, Meal Master), so the page scrolls and the figure
// stands.
const blockWheel = (e) => e.target.blur();

// A "Readymade Nos." item (method_master id 6, e.g. Pav for pavbhaji) is counted in whole
// pieces, not weighed: its RM and FG/Person are both "Nos.", so the kgs<->gms 1000 factor
// that every other item carries collapses to 1. isReadymade(row) drives that switch, and
// foodFactor(row) is the single 1000-or-1 that the RM<->food conversions multiply/divide by.
// The stored historical & planned values still round-trip through *1000 (see histFood and the
// prefill/save code) -- for a Nos item that just recovers the whole number written earlier.
const READYMADE_NOS = 6;
const isReadymade = (row) => num(row.method) === READYMADE_NOS;
const foodFactor = (row) => (isReadymade(row) ? 1 : 1000);

// Column letters below refer to the Estimation sheet.
// Units: RAS has none; for normal items RM is kgs and FG/Person is gms, for Readymade Nos
// items both are "Nos." (see foodFactor above).
// Q ("Historical RM to FG Ratio") drives every RM <-> FG/Person conversion on the row and
// is deliberately not rendered -- it exists only as a calculation input.
const compute = (row) => {
  const pc = num(row.planned_count);                      // C
  const stdRas = num(row.std_ras);                        // D (consumption_master.ras_raw_mat_per_100_person)
  const q = num(row.hist_rm_to_fg_ratio);                 // Q (historical_data.rm_to_fg_ratio)
  const histFood = num(row.hist_fg_per_person_kg) * 1000; // I = historical value (gms, or Nos for readymade)
  const plannedRm = num(row.planned_rm);                  // K (kgs / Nos, editable)
  const plannedFood = num(row.planned_fg_per_person);     // L (gms / Nos, editable)
  const f = foodFactor(row);                              // 1000 normal, 1 for Readymade Nos

  const stdRm = (pc * stdRas) / 100;                                    // E = C*D/100 (kgs / Nos)
  const stdFood = pc > 0 ? ((stdRm * q) / pc) * f : 0;                  // F = (E*Q)/C*f (gms / Nos)
  const histRm = q > 0 && pc > 0 ? (histFood / q) * pc / f : 0;         // H (kgs / Nos)
  const histRas = pc > 0 ? (histRm / pc) * 100 : 0;                     // G
  const plannedRas = pc > 0 ? (plannedRm / pc) * 100 : 0;               // J = K/C*100
  const diff = plannedFood - histFood;                                  // N (gms / Nos)
  const plannedFgKg = (plannedFood * pc) / f;                           // O (kgs / Nos, hidden but saved)

  return { stdRas, stdRm, stdFood, histRas, histRm, histFood, histRatio: q, plannedRas, plannedRm, plannedFood, diff, plannedFgKg };
};

// Planned RM (K) and Planned FG/Person (L) are two views of one plan, tied together by Q:
//   L = (K*Q)/C*f      K = (L/Q)*C/f     (f = foodFactor: 1000 normal, 1 for Readymade Nos)
// Editing either rewrites the other (sheet row 20). Changing the count re-plans from
// FG/Person when it's set -- the per-person amount is the intent, so RM follows the count.
const syncRow = (row, field) => {
  const pc = num(row.planned_count);
  const q = num(row.hist_rm_to_fg_ratio);
  const f = foodFactor(row); // 1000 normal, 1 for Readymade Nos

  // Q = 0 means there is no historical ratio to convert through, so RM and FG/Person aren't
  // two views of one plan any more -- they're independent. Leave whatever was typed alone
  // instead of zeroing FG/Person (toFood with q=0) or blanking RM (toRm's q > 0 guard).
  if (q <= 0) return row;

  const toRm = (food) => (q > 0 ? round0((num(food) / q) * pc / f) : "");
  const toFood = (rm) => (pc > 0 ? round2(((num(rm) * q) / pc) * f) : "");

  if (field === "planned_rm") {
    row.planned_fg_per_person = row.planned_rm === "" ? "" : toFood(row.planned_rm);
  } else if (field === "planned_fg_per_person") {
    row.planned_rm = row.planned_fg_per_person === "" ? "" : toRm(row.planned_fg_per_person);
  } else if (field === "planned_count") {
    if (row.planned_fg_per_person !== "" && row.planned_fg_per_person != null) {
      row.planned_rm = toRm(row.planned_fg_per_person);
    } else if (row.planned_rm !== "" && row.planned_rm != null) {
      row.planned_fg_per_person = toFood(row.planned_rm);
    }
  }
  return row;
};

// ---- The Excel export ----
//
// The sheet follows the toggle: All Columns exports the whole table, Planned Columns exports the
// three Planned figures and the two that name the row. What is on screen is what comes out.
//
// The header is the screen's own two rows -- a band over each block, the column names beneath it,
// and the row-naming columns merged down through both -- so a sheet and the table it came from
// read the same way and "RAS" is never ambiguous about which block it belongs to.
//
// The row-selection checkbox is not a column and does not go. Every loaded row is exported,
// ticked or not: the ticks open the Consumption Report and say nothing about what was planned.
//
// Read through compute() so a cell and the screen cannot disagree, and rounded the way the screen
// rounds each column. Numbers go out as numbers, so the sheet can be summed and sorted.
const numOrBlank = (v, d) => {
  const n = parseFloat(v);
  return isNaN(n) ? "" : Number(n.toFixed(d));
};

export const buildEstimationExport = (rows, { date, session, showAll = true }) => {
  const list = rows ?? [];

  // [band, column, width] per block, in the order the table lays them out.
  const PLANNED_BLOCK = [["RAS", 10], ["RM (Kgs.)", 12], ["FG / Person (gms)", 18]];
  const bandRow = showAll
    ? ["", "", "Standard", "", "", "Historical", "", "", "Planned", "", "",
       "Diff in FG / person (gms)", "Planned FG Qty (kgs)"]
    : ["", "", "Planned", "", ""];
  const nameRow = showAll
    ? ["Meal Item Name", "Planned Count (Persons)",
       ...PLANNED_BLOCK.map(([c]) => c), ...PLANNED_BLOCK.map(([c]) => c),
       ...PLANNED_BLOCK.map(([c]) => c), "", ""]
    : ["Meal Item Name", "Planned Count (Persons)", ...PLANNED_BLOCK.map(([c]) => c)];
  const width = nameRow.length;

  const body = list.map((row) => {
    const c = compute(row);
    // "Nos." is a unit, not a name -- but a readymade row's RM and FG / Person are counts and not
    // kgs and gms, so the sheet has to say which rows those are. The screen marks them with a
    // badge; here it goes after the name.
    const name = `${itemExportName(row)}${isReadymade(row) ? ' (Nos.)' : ''}`;
    // The two editable columns are taken from the boxes rather than through compute(): there an
    // empty box comes back as 0, because num() reads "" as 0, and in a spreadsheet a 0 is a
    // planned nothing where a blank is nothing planned yet.
    const planned = [
      numOrBlank(c.plannedRas, 2), numOrBlank(row.planned_rm, 0),
      numOrBlank(row.planned_fg_per_person, 2),
    ];
    if (!showAll) return [name, numOrBlank(row.planned_count, 0), ...planned];
    return [
      name, numOrBlank(row.planned_count, 0),
      numOrBlank(c.stdRas, 2), numOrBlank(c.stdRm, 0), numOrBlank(c.stdFood, 2),
      numOrBlank(c.histRas, 2), numOrBlank(c.histRm, 0), numOrBlank(c.histFood, 2),
      ...planned,
      numOrBlank(c.diff, 2), numOrBlank(c.plannedFgKg, 2),
    ];
  });

  // The totals the screen shows: FG / Person on each block it shows, over the rows measured in
  // gms. Readymade Nos rows are counted in "Nos." and are left out, exactly as on screen.
  const foodTotal = (pick) => numOrBlank(
    list.filter((r) => !isReadymade(r)).reduce((t, r) => t + pick(compute(r)), 0), 2);
  const totalRow = showAll
    ? ["Total", "", "", "", foodTotal((c) => c.stdFood), "", "", foodTotal((c) => c.histFood),
       "", "", foodTotal((c) => c.plannedFood), "", ""]
    : ["Total", "", "", "", foodTotal((c) => c.plannedFood)];

  // Bands span their three columns; the columns that name a row are merged down through both
  // header rows, the way the table gives them rowSpan="2".
  const band = (c) => ({ s: { r: 3, c }, e: { r: 3, c: c + 2 } });
  const downBoth = (c) => ({ s: { r: 3, c }, e: { r: 4, c } });
  return {
    aoa: [
      [`Production Estimation (${showAll ? 'All Columns' : 'Planned Columns'})`],
      [`Date : ${date ?? ''}`, "", `Session : ${session?.label ?? ''}`],
      [],
      bandRow,
      nameRow,
      ...body,
      totalRow,
    ],
    cols: [{ wch: 34 }, { wch: 22 }, ...(showAll ? PLANNED_BLOCK.concat(PLANNED_BLOCK) : [])
      .map(([, wch]) => ({ wch })), ...PLANNED_BLOCK.map(([, wch]) => ({ wch })),
      ...(showAll ? [{ wch: 24 }, { wch: 20 }] : [])],
    merges: [
      { s: { r: 0, c: 0 }, e: { r: 0, c: width - 1 } },   // title across the sheet
      { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },           // Date
      { s: { r: 1, c: 2 }, e: { r: 1, c: width - 1 } },   // Session
      downBoth(0), downBoth(1),                           // Meal Item Name, Planned Count
      ...(showAll
        ? [band(2), band(5), band(8), downBoth(11), downBoth(12)]
        : [band(2)]),
    ],
  };
};

function ProductionEstimation() {
  const [date, setDate] = useState("");
  const [session, setSession] = useState("");
  const [plannedCount, setPlannedCount] = useState("");
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Which columns the table shows. "all" is every section, as before; "planned" narrows it to
  // the three Planned columns and the two that identify the row, so the figures being typed sit
  // together instead of at the far right of a table that scrolls sideways. A view, not a filter:
  // the hidden columns are still computed, still totalled where they were, and still saved.
  const [columnView, setColumnView] = useState("all");
  const showAll = columnView === "all";

  const handleFilterChange = async (selectedDate, selectedSession) => {
    const sessionId = selectedSession?.value;
    if (!selectedDate || !sessionId) {
      toast.error("Please select date and session");
      return;
    }
    setDate(selectedDate);
    setSession(selectedSession);
    setLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/production_estimation/item_list?date=${selectedDate}&session=${sessionId}`
      );
      const data = response.data.data ?? [];
      setRows(
        data.map((item) => ({
          menu_item_id: item.menu_item_id,
          item_name: item.item_name,
          eng_name: item.eng_name,
          method: item.method, // method_master id; 6 = Readymade Nos (see foodFactor)
          std_ras: item.std_ras,
          hist_fg_per_person_kg: item.hist_fg_per_person_kg,
          hist_rm_to_fg_ratio: item.hist_rm_to_fg_ratio,
          // prefill planned values from raw_material: RM stays kg, FG/Person kg -> gms
          planned_count: wholeNumber(item.planned_count != null ? item.planned_count : plannedCount),
          planned_rm: item.planned_rm_kg != null ? round0(num(item.planned_rm_kg)) : "",
          planned_fg_per_person: item.planned_fg_per_person_kg != null ? round2(num(item.planned_fg_per_person_kg) * 1000) : "",
        }))
      );
      setSubmitted(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load meal items");
    } finally {
      setLoading(false);
    }
  };

  // Top "Planned Count" applies to every row (each row still individually editable).
  const handlePlannedCountChange = (value) => {
    const count = wholeNumber(value);
    setPlannedCount(count);
    setRows((prev) => prev.map((r) => syncRow({ ...r, planned_count: count }, "planned_count")));
  };

  const toggleRow = (index) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    setChecked((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((_, i) => i))));
  };

  // Opens the Consumption Report in a new tab for the checked meal items, over the trailing
  // two years rather than the planned date.
  const handleCheckRas = async () => {
    const selected = rows.filter((_, i) => checked.has(i));
    if (!selected.length) {
      toast.error("Please select at least one meal item");
      return;
    }
    if (!date) {
      toast.error("Please select date and session");
      return;
    }

    // The day being planned has nothing consumed against it yet, and the RAS / consumption
    // figures are read as a trend across every past time these items were served -- so the
    // window ends the day before the selected session date and reaches back two years from it.
    await openConsumptionReport(
      date,
      selected.map((r) => r.menu_item_id),
      session,
      trailingTwoYearsBeforeSession(date)
    );
  };

  const handleRowChange = (index, field, value) => {
    // Planned Count (persons) and Planned RM (kgs) are both whole-number columns; FG / Person
    // carries 2 decimals.
    const val =
      field === "planned_count" || field === "planned_rm"
        ? wholeNumber(value)
        : field === "planned_fg_per_person"
        ? twoDecimals(value)
        : value;
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = syncRow({ ...updated[index], [field]: val }, field);
      return updated;
    });
  };

  const handleSave = async () => {
    if (!date || !session?.value) {
      toast.error("Please select date and session");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        date,
        session,
        items: rows.map((r) => {
          const c = compute(r);
          return {
            menu_item_id: r.menu_item_id,
            planned_count: r.planned_count,
            planned_rm_kg: num(r.planned_rm),                              // already kgs
            planned_fg_per_person_kg: num(r.planned_fg_per_person) / 1000, // gms -> kg
            planned_ras: c.plannedRas,
            planned_fg_kg: c.plannedFgKg,
            std_ras: c.stdRas,
            hist_rm_to_fg_ratio: c.histRatio,
          };
        }),
      };
      const response = await axios.post(`${baseUrl}/production_estimation/save`, payload);
      if (response.data.status) {
        toast.success(response.data.msg || "Saved successfully");
      } else {
        toast.error(response.data.msg || "Save failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const editCell = "block w-[70px] p-1 text-sm text-gray-900 border border-gray-300 rounded bg-yellow-50 focus:ring-blue-500 focus:border-blue-500";
  const roCell = "px-3 py-2 text-right";
  // Narrow read-only cell -- tighter padding for the columns that were asked to be slimmed down.
  const roCellTight = "px-2 py-2 text-right";
  // A heavier vertical rule (div) marks every section boundary so all six blocks read as distinct.
  // The Standard / Historical / Planned group headers keep their own slight-dark tint (below);
  // the body columns stay unshaded.
  const div = "pe-divider";

  const handleExport = () => {
    // showAll is the toggle, so the sheet is whatever the screen is showing.
    const sheet = buildEstimationExport(rows, { date, session, showAll });
    const ws = XLSX.utils.aoa_to_sheet(sheet.aoa);
    ws['!cols'] = sheet.cols;
    ws['!merges'] = sheet.merges;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, showAll ? 'All Columns' : 'Planned');
    XLSX.writeFile(wb, `Production_Estimation_${date}_${session?.label ?? ''}.xlsx`);
  };

  // Save sits both above and below the table so a long item list never needs scrolling to save.
  const saveBar = (
    <div className="flex justify-end items-center gap-3 p-4">
      <span className="text-sm text-gray-600 mr-auto">
        Selected: <span className="font-semibold">{checked.size}</span> of {rows.length}
      </span>
      <button
        onClick={handleSave}
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
      >
        Save
      </button>
    </div>
  );

  return (
    <div className="">
      {loading && <Loader />}
      <div className="flex justify-between items-center py-4 px-6">
        <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Production Estimation</h1>
        <Toaster position="top-center" reverseOrder={false} />
      </div>
      <div className="relative shadow-md sm:rounded-lg">
        <div className="flex flex-wrap items-center gap-4 px-4">
          <DateSessionFilter handleFilterChange={handleFilterChange} />
          <div className="m-2">
            <label className="block text-xs font-medium text-gray-700">Planned Count (Persons)</label>
            <input
              type="number"
              step="1"
              min="0"
              onWheel={blockWheel}
              className="block w-40 p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
              value={plannedCount}
              onKeyDown={blockWholeNumberKeys}
              onChange={(e) => handlePlannedCountChange(e.target.value)}
            />
          </div>
          {submitted && rows.length > 0 && (
            <button
              onClick={handleCheckRas}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
            >
              Check RAS / Consumption
            </button>
          )}
          {submitted && rows.length > 0 && (
            // A segmented pair rather than a checkbox: both states are named, so it reads as a
            // choice between two views instead of something switched off.
            <div className="inline-flex rounded-md border border-gray-300 overflow-hidden" role="group">
              {[["all", "All Columns"], ["planned", "Planned Columns"]].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setColumnView(value)}
                  aria-pressed={columnView === value}
                  className={"px-4 py-2 text-sm focus:outline-none focus:ring focus:ring-gray-200 " + (
                    columnView === value
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {submitted && rows.length > 0 && (
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring focus:ring-green-200"
            >
              Export
            </button>
          )}
        </div>

        {submitted && (
          <div className="p-4 overflow-x-auto">
            {/* Dark vertical separators between the sections
                (Meal Item | Planned Count | Standard | Historical | Planned | Diff) must stay
                visible at EVERY browser zoom (100% / 90% / 50% ...). Chrome DROPS collapsed table
                borders at fractional zoom (they show only at 100%), so we use SEPARATE borders
                (border-spacing 0) — each cell paints its own border and none get dropped — with a
                thick, near-black left border on each section's first cell. */}
            <style>{`
              .pe-prod-table { border-collapse: separate; border-spacing: 0; }
              .pe-prod-table .pe-divider { border-left: 3px solid #111827; }
            `}</style>
            {rows.length > 0 ? (
              <>
                {saveBar}
                <table className="w-full text-sm text-gray-600 border pe-prod-table">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th rowSpan="2" className="px-2 py-2 border">
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && checked.size === rows.length}
                          onChange={toggleAll}
                        />
                      </th>
                      <th rowSpan="2" className="px-3 py-2 text-left border">Meal Item Name</th>
                      <th rowSpan="2" className={"px-3 py-2 border " + div}>Planned Count<br /><span className="normal-case font-normal">(Persons)</span></th>
                      {showAll && (
                        <>
                          <th colSpan="3" className={"px-3 py-2 border text-center bg-gray-200 " + div}>Standard</th>
                          <th colSpan="3" className={"px-3 py-2 border text-center bg-blue-100 " + div}>Historical</th>
                        </>
                      )}
                      <th colSpan="3" className={"px-3 py-2 border text-center bg-green-100 " + div}>Planned</th>
                      {showAll && (
                        <>
                          <th rowSpan="2" className={"px-2 py-2 border w-[88px] " + div}>Diff in FG / person<br /><span className="normal-case font-normal text-sm">(gms.)</span></th>
                          <th rowSpan="2" className={"px-2 py-2 border w-[92px] " + div}>Planned FG Qty<br /><span className="normal-case font-normal">(kgs.)</span></th>
                        </>
                      )}
                    </tr>
                    <tr>
                      {showAll && (
                        <>
                          <th className={"px-2 py-1 border " + div}>RAS</th>
                          <th className="px-2 py-1 border">RM (Kgs.)</th>
                          <th className="px-2 py-1 border w-[86px]">FG / Person (gms)</th>
                          <th className={"px-2 py-1 border " + div}>RAS</th>
                          <th className="px-2 py-1 border">RM (Kgs.)</th>
                          <th className="px-2 py-1 border w-[86px]">FG / Person (gms)</th>
                        </>
                      )}
                      <th className={"px-2 py-1 border " + div}>RAS</th>
                      <th className="px-2 py-1 border w-[88px]">RM (Kgs.)</th>
                      <th className="px-2 py-1 border w-[86px]">FG / Person (gms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const c = compute(row);
                      return (
                        <tr
                          key={row.menu_item_id}
                          className={`border-b ${checked.has(index) ? "bg-amber-100" : "bg-white"}`}
                        >
                          <td className="px-2 py-2 border text-center">
                            <input
                              type="checkbox"
                              checked={checked.has(index)}
                              onChange={() => toggleRow(index)}
                            />
                          </td>
                          <td className="px-3 py-2 border font-medium text-gray-900">
                            <ItemNameStacked item={row} />
                            {isReadymade(row) && (
                              <span className="ml-1 px-1.5 py-0.5 text-xs font-normal rounded bg-purple-100 text-purple-700">
                                Nos.
                              </span>
                            )}
                          </td>
                          <td className={"px-2 py-2 border " + div}>
                            <input
                              type="number" step="1" min="0" className={editCell}
                              value={row.planned_count}
                              onWheel={blockWheel}
                              onKeyDown={blockWholeNumberKeys}
                              onChange={(e) => handleRowChange(index, "planned_count", e.target.value)}
                            />
                          </td>
                          {showAll && (
                            <>
                              {/* Standard */}
                              <td className={roCell + " border " + div}>{fmt(c.stdRas)}</td>
                              <td className={roCell + " border"}>{round0(c.stdRm)}</td>
                              <td className={roCell + " border"}>{fmt2(c.stdFood)}</td>
                              {/* Historical */}
                              <td className={roCell + " border " + div}>{fmt(c.histRas)}</td>
                              <td className={roCell + " border"}>{round0(c.histRm)}</td>
                              <td className={roCell + " border"}>{fmt2(c.histFood)}</td>
                            </>
                          )}
                          {/* Planned (RM & Food editable) */}
                          <td className={roCell + " border " + div}>{fmt(c.plannedRas)}</td>
                          <td className="px-2 py-2 border">
                            <input
                              type="number" step="1" min="0" className={editCell}
                              value={row.planned_rm}
                              onWheel={blockWheel}
                              onKeyDown={blockWholeNumberKeys}
                              onChange={(e) => handleRowChange(index, "planned_rm", e.target.value)}
                            />
                          </td>
                          <td className="px-2 py-2 border">
                            <input
                              type="number" step="any" className={editCell}
                              value={row.planned_fg_per_person}
                              onWheel={blockWheel}
                              onKeyDown={blockArrowKeys}
                              onChange={(e) => handleRowChange(index, "planned_fg_per_person", e.target.value)}
                            />
                          </td>
                          {showAll && (
                            <>
                              {/* Diff runs a step larger than the rest of the table -- at text-sm
                                  the leading minus sign on a negative diff was hard to pick out. */}
                              <td className={roCellTight + " border text-base " + div + " " + (c.diff < 0 ? "text-red-600" : "")}>{fmt(c.diff)}</td>
                              <td className={roCellTight + " border " + div}>{fmt(c.plannedFgKg)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-semibold text-gray-800">
                      {/* Totals sum FG/Person in gms only -- Readymade Nos rows are counted in
                          "Nos." and excluded, matching the sheet where Pav sits below the total.
                          Cells are split so the section dividers line up with the body. */}
                      <td className="px-3 py-2 border text-right" colSpan="3">Total</td>
                      {showAll && (
                        <>
                          <td className={"border " + div} colSpan="2"></td>
                          <td className={roCell + " border"}>
                            {fmt(rows.filter((r) => !isReadymade(r)).reduce((s, r) => s + compute(r).stdFood, 0))}
                          </td>
                          <td className={"border " + div} colSpan="2"></td>
                          <td className={roCell + " border"}>
                            {fmt(rows.filter((r) => !isReadymade(r)).reduce((s, r) => s + compute(r).histFood, 0))}
                          </td>
                        </>
                      )}
                      <td className={"border " + div} colSpan="2"></td>
                      <td className={roCell + " border"}>
                        {fmt(rows.filter((r) => !isReadymade(r)).reduce((s, r) => s + compute(r).plannedFood, 0))}
                      </td>
                      {showAll && (
                        <>
                          <td className={"border " + div}></td>
                          <td className={"border " + div}></td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                </table>
                {saveBar}
              </>
            ) : (
              <NoRecordsFound />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductionEstimation;
